import { redirectToLogin, getLastTenantSlug } from '../utils/sessionNavigation.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL
    ? (import.meta.env.VITE_API_BASE_URL.endsWith('/api') 
        ? import.meta.env.VITE_API_BASE_URL 
        : `${import.meta.env.VITE_API_BASE_URL}/api`)
    : '/api';

function appendSuperadminTenantQuery(endpoint) {
    try {
        const user = JSON.parse(localStorage.getItem('alinea_user') || 'null');
        if (user?.rol !== 'superadmin') return endpoint;
        const tid = localStorage.getItem('alinea_superadmin_tenant_id');
        if (!tid || endpoint.includes('tenant_id=')) return endpoint;
        const sep = endpoint.includes('?') ? '&' : '?';
        return `${endpoint}${sep}tenant_id=${encodeURIComponent(tid)}`;
    } catch {
        return endpoint;
    }
}

async function request(endpoint, options = {}) {
    const token = localStorage.getItem('alinea_token');
    const url = appendSuperadminTenantQuery(endpoint);

    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
        },
        ...options,
    };

    if (config.body && typeof config.body === 'object') {
        config.body = JSON.stringify(config.body);
    }

    const response = await fetch(`${API_BASE}${url}`, config);

    if (response.status === 504) {
        throw new Error(
            'El servidor tardó demasiado en responder (504). Espera unos segundos y vuelve a intentar.'
        );
    }
    if (response.status === 502 || response.status === 503) {
        throw new Error('El servicio no está disponible temporalmente. Intenta de nuevo en un momento.');
    }

    if (response.status === 401) {
        const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/me');
        const onLoginPage =
            typeof window !== 'undefined' && window.location.pathname.startsWith('/login');
        if (!isAuthRoute && !onLoginPage) {
            let sessionUser = null;
            try {
                sessionUser = JSON.parse(localStorage.getItem('alinea_user') || 'null');
            } catch {
                sessionUser = null;
            }
            localStorage.removeItem('alinea_token');
            localStorage.removeItem('alinea_user');
            redirectToLogin({
                user: sessionUser,
                fallbackSlug: getLastTenantSlug(),
            });
        }
        throw new Error(isAuthRoute ? 'No autorizado' : 'Sesión expirada');
    }

    const contentType = response.headers.get('content-type') || '';
    let data;
    if (contentType.includes('application/json')) {
        data = await response.json();
    } else {
        const text = await response.text();
        if (!response.ok) {
            const snippet = text.replace(/<[^>]+>/g, ' ').trim().slice(0, 120);
            throw new Error(snippet || `Error del servidor (${response.status})`);
        }
        try {
            data = JSON.parse(text);
        } catch {
            throw new Error('Respuesta inválida del servidor (no es JSON).');
        }
    }

    if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Error del servidor');
    }

    return data;
}

export const api = {
    // Auth
    login: (employeeCode) => request('/auth/login', { method: 'POST', body: { employeeCode } }),
    me: () => request('/auth/me'),

    // Surveys (Diagnóstico)
    submitDailySurvey: (data) => request('/diagnostico/daily', { method: 'POST', body: data }),
    getTodaySurvey: () => request('/diagnostico/daily/today'),
    getDailyHistory: () => request('/diagnostico/mi-historial'),
    submitWeeklySurvey: (data) => request('/diagnostico/weekly', { method: 'POST', body: data }),
    getCurrentWeekly: () => request('/diagnostico/weekly/current'),
    getHistory: () => request('/diagnostico/mi-historial'), // Changed from daily/history
    submitProfileSurvey: (answers) => request('/diagnostico/profile', { method: 'POST', body: { answers } }),
    /** Mismo endpoint que `submitProfileSurvey`; alias usado por Registro de manada (`CuestionarioPerfil`). */
    submitProfileQuiz: (answers) => request('/diagnostico/profile', { method: 'POST', body: { answers } }),
    getProfile: () => request('/diagnostico/profile'),
    getRegistroManadaEstado: () => request('/diagnostico/registro-manada-estado'),
    getProfileVersions: () => request('/diagnostico/profile-versions'),
    getProfileEvolutionMasters: () => request('/diagnostico/profile-evolution-masters'),
    getHumanCycleConfig: () => request('/diagnostico/human-cycles/config'),
    getHumanCyclesAdmin: () => request('/diagnostico/human-cycles/admin'),
    saveHumanCycleConfig: (body) =>
        request('/diagnostico/human-cycles/config', { method: 'PUT', body }),
    resetHumanCycleConfig: () => request('/diagnostico/human-cycles/reset', { method: 'POST' }),

    // Estado Funcional (flags OFF por defecto; UI solo si efCoreEnabled)
    getFunctionalStateStatus: () => request('/functional-state/status'),
    putFunctionalFlags: (body) => request('/functional-state/flags', { method: 'PUT', body }),
    postFunctionalConsent: (body) => request('/functional-state/consents', { method: 'POST', body }),
    postFunctionalSnapshot: (body) => request('/functional-state/snapshots', { method: 'POST', body }),
    /** Índices de voz → proxy labs CyE (sin audio). Opcional si el lab no está arriba. */
    postLabsVoiceIndices: (body) => request('/labs/voice-indices', { method: 'POST', body }),
    getFunctionalSnapshots: (query = '') =>
        request(`/functional-state/snapshots${query ? `?${query}` : ''}`),
    getFunctionalEvolution: (query = '') =>
        request(`/functional-state/evolution${query ? `?${query}` : ''}`),
    eraseFunctionalVectors: (id) =>
        request(`/functional-state/snapshots/${id}/vectors`, { method: 'DELETE' }),
    getCipSummary: () => request('/analytical-models/cip'),
    getManadaDynamics: () => request('/analytical-models/manada'),
    getAnalyticalSuite: () => request('/analytical-models/suite'),
    getBienestarSummary: () => request('/analytical-models/bienestar'),
    getRiesgoSummary: () => request('/analytical-models/riesgo'),
    getLiderazgoSummary: () => request('/analytical-models/liderazgo'),
    getAdaptacionSummary: () => request('/analytical-models/adaptacion'),
    getCidWrap: () => request('/analytical-models/cid'),
    getPreventiveQueue: () => request('/preventive/queue'),
    proposePreventive: () => request('/preventive/propose', { method: 'POST', body: {} }),
    preventiveFollowUp: (alertId, data) =>
        request(`/preventive/${alertId}/follow-up`, { method: 'POST', body: data }),

    // Admin Diagnostico
    getAdminSurveySummary: () => request('/diagnostico/admin-summary'),
    getProfileQuestions: (scope) =>
        request(scope === 'tenant' ? '/diagnostico/profile-questions?scope=tenant' : '/diagnostico/profile-questions'),
    createProfileQuestion: (data) => request('/diagnostico/profile-questions', { method: 'POST', body: data }),
    updateProfileQuestion: (id, data) => request(`/diagnostico/profile-questions/${id}`, { method: 'PUT', body: data }),
    deleteProfileQuestion: (id) => request(`/diagnostico/profile-questions/${id}`, { method: 'DELETE' }),
    resetProfileQuestions: () => request('/diagnostico/profile-questions/reset-default', { method: 'POST' }),
    getMapaManada: () => request('/diagnostico/mapa-manada'),
    updateBirthDate: (id, birth_date) => request(`/diagnostico/birth-date/${id}`, { method: 'PATCH', body: { birth_date } }),

    // Alerts (Alertas)
    getAlerts: () => request('/alertas'),
    getAlertRules: () => request('/alertas/rules'),
    getAlertSummary: () => request('/alertas/summary'),
    getEmailReportSettings: () => request('/admin/email-reports'),
    saveEmailReportSettings: (body) => request('/admin/email-reports', { method: 'PUT', body }),
    sendEmailReportNow: (audience) =>
        request('/admin/email-reports/send-now', { method: 'POST', body: { audience } }),
    acknowledgeAlert: (id) => request(`/alertas/${id}/acknowledge`, { method: 'PATCH' }),
    interveneAlert: (id, data) => request(`/alertas/${id}/intervene`, { method: 'POST', body: data }),
    getInterventions: (id) => request(`/alertas/${id}/interventions`),

    // Messages (Mensajes)
    getMessages: () => request('/messages/hoy'),
    getMessageHistory: () => request('/messages/history'),
    submitFeedback: (deliveryId, feedback) => request(`/messages/${deliveryId}/feedback`, {
        method: 'POST',
        body: { feedback }
    }),
    // Admin Message Methods
    getAdminMessages: () => request('/messages'),
    createMessage: (data) => request('/messages', { method: 'POST', body: data }),
    updateMessage: (id, data) => request(`/messages/${id}`, { method: 'PUT', body: data }),
    deleteMessage: (id) => request(`/messages/${id}`, { method: 'DELETE' }),
    getAdminFeedback: () => request('/messages/admin/feedback'),
    getMessagesCompanySummary: () => request('/messages/company-summary'),
    ensureMessageBank: () => request('/messages/ensure-bank', { method: 'POST' }),

    // Employees
    getEmployees: (tenantId, { lite = false } = {}) => {
        const extra = lite ? { lite: '1' } : {};
        return request(`/employees${api._calcTenantQs(tenantId, extra)}`);
    },
    getEmployee: (id) => request(`/employees/${id}`),
    createEmployee: (data) => request('/employees', { method: 'POST', body: data }),
    /** Asigna en BD `monthly_salary` a empleados activos del tenant que tengan sueldo null o 0 (p. ej. COP). */
    bulkAssignDefaultSalary: (monthly_salary = 2_000_000) =>
        request('/employees/bulk-default-salary', { method: 'POST', body: { monthly_salary } }),
    updateEmployee: (id, data) => request(`/employees/${id}`, { method: 'PUT', body: data }),
    getAreaSummary: () => request('/employees/areas/summary'),
    getSupervisors: () => request('/employees/supervisors/list'),
    downloadEmployeeTemplate: async () => {
        const token = localStorage.getItem('alinea_token');
        const res = await fetch(`${API_BASE}${appendSuperadminTenantQuery('/employees/download/template')}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Error al descargar plantilla');
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Plantilla_ALINEA_Personal.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    },
    downloadEmployeeRosterExport: async () => {
        const token = localStorage.getItem('alinea_token');
        const res = await fetch(`${API_BASE}${appendSuperadminTenantQuery('/employees/export/roster')}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Error al exportar personal');
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ALINEA_Base_Personal.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    },
    uploadEmployees: async (file) => {
        const token = localStorage.getItem('alinea_token');
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}${appendSuperadminTenantQuery('/employees/upload')}`, {
            method: 'POST',
            body: formData,
            headers: { 'Authorization': `Bearer ${token}` }
        });
        let data = {};
        try {
            data = await res.json();
        } catch {
            data = {};
        }
        if (!res.ok) {
            const err = new Error(data.error || `Error al subir (${res.status})`);
            err.errors = data.errors;
            err.warnings = data.warnings;
            err.status = res.status;
            throw err;
        }
        return data;
    },

    // EPA
    submitCheckin: (data) => request('/epa/checkin', { method: 'POST', body: data }),
    recordPause: () => request('/epa/checkin/pause', { method: 'POST' }),
    getCheckins: () => request('/epa/checkins'),
    submitIncident: (data) => request('/epa/incidents', { method: 'POST', body: data }),
    getLessons: () => request('/epa/incidents/lessons'),
    registerChange: (data) => request('/epa/changes', { method: 'POST', body: data }),
    markAnchorDone: (id) => request(`/epa/changes/${id}/anchor`, { method: 'PATCH' }),
    getChanges: () => request('/epa/changes'),
    getEpaCompanySummary: () => request('/epa/company-summary'),

    /** Query string con tenant_id (súper admin o llamadas explícitas). */
    _tenantQs(tenantId, extra = {}) {
        const q = new URLSearchParams(extra);
        if (tenantId != null && tenantId !== '') q.set('tenant_id', String(tenantId));
        const s = q.toString();
        return s ? `?${s}` : '';
    },
    _calcTenantQs(tenantId, extra = {}) {
        return api._tenantQs(tenantId, extra);
    },
    getCosts: (tenantId) => request(`/calculator/costs${api._calcTenantQs(tenantId)}`),
    getCalculatorSalaryAverages: (areaId, positionLevel = 'operativo', tenantId) => {
        const q = { area_id: String(areaId), position_level: String(positionLevel) };
        return request(`/calculator/salary-averages${api._calcTenantQs(tenantId, q)}`);
    },
    simulate: (data, tenantId) => {
        const body = tenantId != null ? { ...data, tenantId } : data;
        const qs = tenantId != null ? `?tenant_id=${tenantId}` : '';
        return request(`/calculator/simulate${qs}`, { method: 'POST', body });
    },
    getFinancialDashboard: (tenantId) => request(`/calculator/dashboard${api._calcTenantQs(tenantId)}`),
    getRetirementHistory: (tenantId) => request(`/calculator/retirement-history${api._calcTenantQs(tenantId)}`),
    getIndicatorsDashboard: () => request('/admin/indicators'),
    updateCosts: (data, tenantId) => {
        const body = tenantId != null ? { ...data, tenantId } : data;
        const qs = tenantId != null ? `?tenant_id=${tenantId}` : '';
        return request(`/calculator/costs${qs}`, { method: 'PUT', body });
    },
    deleteCalculatorCosts: (areaId, positionLevel, tenantId) => {
        const q = {
            area_id: String(areaId),
            position_level: String(positionLevel),
        };
        return request(`/calculator/costs${api._calcTenantQs(tenantId, q)}`, { method: 'DELETE' });
    },
    getAdminAreas: (tenantId) => request(`/admin/areas${api._calcTenantQs(tenantId)}`),
    createAdminArea: (data, tenantId) =>
        request(`/admin/areas${api._calcTenantQs(tenantId)}`, { method: 'POST', body: data }),
    updateAdminArea: (areaId, data, tenantId) =>
        request(`/admin/areas/${areaId}${api._calcTenantQs(tenantId)}`, { method: 'PUT', body: data }),
    deleteAdminArea: (areaId, tenantId) =>
        request(`/admin/areas/${areaId}${api._calcTenantQs(tenantId)}`, { method: 'DELETE' }),

    // Onboarding maestro (super admin)
    downloadOnboardingTemplate: async () => {
        const token = localStorage.getItem('alinea_token');
        const res = await fetch(`${API_BASE}/superadmin/onboarding/template`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
        });

        const bytes = new Uint8Array(await res.arrayBuffer());

        if (!res.ok) {
            let msg = 'No se pudo descargar la plantilla';
            try {
                const j = JSON.parse(new TextDecoder().decode(bytes));
                msg = j.error || msg;
            } catch {
                const txt = new TextDecoder().decode(bytes).trim();
                if (txt) msg = txt.slice(0, 200);
            }
            throw new Error(msg);
        }

        if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
            const preview = new TextDecoder().decode(bytes.slice(0, 120));
            if (preview.trimStart().startsWith('{') || preview.trimStart().startsWith('<')) {
                throw new Error(
                    'El servidor no devolvió un Excel válido (sesión expirada o API detenida). Inicie el servidor en el puerto 3001 e inicie sesión como súper admin (ADM001).'
                );
            }
            throw new Error('El archivo descargado está dañado o vacío. Reinicie la API e intente de nuevo.');
        }

        const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ALINEA_Plantilla_Implementacion.xlsx';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    },
    uploadOnboardingFile: async (file, { dryRun = true } = {}) => {
        const token = localStorage.getItem('alinea_token');
        const form = new FormData();
        form.append('file', file);
        const qs = dryRun ? '?dry_run=1' : '';
        const res = await fetch(`${API_BASE}/superadmin/onboarding/import${qs}`, {
            method: 'POST',
            body: form,
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok && !data.errors) {
            throw new Error(data.error || 'No se pudo procesar el archivo');
        }
        return data;
    },

    // Systemic Friction Map
    getFrictionMapSummary: () => request('/friction-map/summary'),
    getPreventiveCid: () => request('/friction-map/cid-preventive'),
    getFrictionArea: (areaId) => request(`/friction-map/areas/${areaId}`),
    getFrictionSupervisor: (supervisorId) => request(`/friction-map/supervisors/${supervisorId}`),
    getFrictionWorker: (workerId) => request(`/friction-map/workers/${workerId}`),
    getMyObservadorRecord: () => request('/friction-map/mi-registro'),
    analyzeObservadorForm: (data) => request('/friction-map/analyze-form', { method: 'POST', body: data }),

    // Coach
    submitLeaderProfile: (answers) => request('/coach/profile', { method: 'POST', body: { answers } }),
    getLeaderProfile: () => request('/coach/profile'),
    getCoachingGuides: () => request('/coach/guides'),
    getCoachingGuide: (profile) => request(`/coach/guides/${profile}`),
    getPowerfulQuestions: () => request('/coach/questions'),
    getCoachQuestionsBank: () => request('/coach/questions/bank'),
    ensureCoachQuestionBank: () => request('/coach/questions/ensure-bank', { method: 'POST' }),
    createCoachQuestion: (body) => request('/coach/questions', { method: 'POST', body }),
    updateCoachQuestion: (id, body) => request(`/coach/questions/${id}`, { method: 'PUT', body }),
    deleteCoachQuestion: (id) => request(`/coach/questions/${id}`, { method: 'DELETE' }),
    getCoachTrainingCurriculum: () => request('/coach/training-curriculum'),
    getCoachImprovementPlan: (supervisorId) =>
        request(supervisorId ? `/coach/improvement-plan/${supervisorId}` : '/coach/improvement-plan'),
    getCoachImprovementPlans: () => request('/coach/improvement-plans'),
    
    // Retirement (Causales)
    getRetirementReasons: () => request('/retirement-reasons'),
    createRetirementReason: (data) => request('/retirement-reasons', { method: 'POST', body: data }),
    deleteRetirementReason: (id) => request(`/retirement-reasons/${id}`, { method: 'DELETE' }),
    retireEmployee: (id, data) => request(`/employees/${id}/retire`, { method: 'POST', body: data }),

    // Súper admin — empresas (marca blanca)
    getSuperadminTenants: () => request('/superadmin/tenants'),
    createSuperadminTenant: (body) => request('/superadmin/tenants', { method: 'POST', body }),
    setSuperadminTenantActive: (id, is_active) =>
        request(`/superadmin/tenants/${id}`, { method: 'PATCH', body: { is_active } }),
    setSuperadminSolutionProfile: (id, solutionProfile) =>
        request(`/superadmin/tenants/${id}/solution-profile`, {
            method: 'PUT',
            body: { solutionProfile },
        }),
    deleteSuperadminTenant: (id, confirm_slug) =>
        request(`/superadmin/tenants/${id}`, { method: 'DELETE', body: { confirm_slug } }),
    getSuperadminFinance: (periodYm) =>
        request(`/superadmin/finance${periodYm ? `?periodYm=${encodeURIComponent(periodYm)}` : ''}`),
    patchSuperadminTenantCommercial: (id, body) =>
        request(`/superadmin/tenants/${id}/commercial`, { method: 'PATCH', body }),
    addSuperadminOpex: (body) => request('/superadmin/finance/opex', { method: 'POST', body }),
    deleteSuperadminOpex: (id) => request(`/superadmin/finance/opex/${id}`, { method: 'DELETE' }),
    generateSuperadminInvoices: (body) =>
        request('/superadmin/finance/invoices/generate', { method: 'POST', body }),
    setSuperadminInvoiceStatus: (id, status) =>
        request(`/superadmin/finance/invoices/${id}/status`, {
            method: 'PATCH',
            body: { status },
        }),

    // Operational intelligence (admin)
    getOpsSnapshot: () => request('/ops/snapshot'),
    getOpsHistory: (limit = 24) => request(`/ops/history?limit=${limit}`),
    postOpsRecord: (body) => request('/ops/record', { method: 'POST', body }),

    // Generic (for future-proofing)
    get: (url) => request(url),
    post: (url, data) => request(url, { method: 'POST', body: data }),
    put: (url, data) => request(url, { method: 'PUT', body: data }),
    delete: (url) => request(url, { method: 'DELETE' }),
};
